import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Shield, LayoutDashboard, Users, CreditCard, Key, Bell, FileText, Flag, ArrowLeft, LogOut, Menu, X, DollarSign, Wallet, Tag, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const adminNavGroups = [
  {
    label: null, // no header for first group
    items: [
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    ],
  },
  {
    label: 'Revenue',
    items: [
      { path: '/admin/payments', icon: CreditCard, label: 'Payments' },
      { path: '/admin/pricing', icon: DollarSign, label: 'Pricing' },
      { path: '/admin/ea-store', icon: Package, label: 'EA Store' },
      { path: '/admin/coupons', icon: Tag, label: 'Coupons' },
      { path: '/admin/wallets', icon: Wallet, label: 'Wallets' },
      { path: '/admin/billing', icon: CreditCard, label: 'Billing' },
    ],
  },
  {
    label: 'Users',
    items: [
      { path: '/admin/users', icon: Users, label: 'Users' },
      { path: '/admin/api-keys', icon: Key, label: 'API Keys' },
      { path: '/admin/announcements', icon: Bell, label: 'Announcements' },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/admin/feature-flags', icon: Flag, label: 'Feature Flags' },
      { path: '/admin/logs', icon: FileText, label: 'Logs' },
    ],
  },
];

const RED_GRADIENT = 'linear-gradient(135deg, #f87171, #f97316)';

export default function AdminSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const sidebarContent = (
    <div className="flex flex-col h-full relative">
      {/* Ambient red glow orbs */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full opacity-[0.07] blur-3xl pointer-events-none bg-red-500" />
      <div className="absolute bottom-24 left-1/4 w-24 h-24 rounded-full opacity-[0.05] blur-2xl pointer-events-none bg-orange-500" />

      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70" />

      {/* Brand */}
      <div className="px-5 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: RED_GRADIENT, boxShadow: '0 4px 16px rgba(239,68,68,0.3)' }}>
            <Shield size={17} className="text-white" />
          </div>
          <div>
            <h1 className="font-display text-red-400 font-bold text-[15px] leading-tight">Admin Panel</h1>
            <p className="text-[9px] text-[var(--text-dim)] font-medium tracking-wider uppercase">TrackMyFxEA</p>
          </div>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden p-1.5 text-[var(--text-dim)] hover:text-[var(--text-primary)] rounded-md hover:bg-white/5">
          <X size={18} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {adminNavGroups.map((group, gi) => (
          <div key={gi} className={gi > 0 ? 'mt-4' : ''}>
            {group.label && (
              <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-dim)]">
                {group.label}
              </div>
            )}
            {group.items.map(({ path, icon: Icon, label, end }) => (
              <NavLink
                key={path}
                to={path}
                end={end}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 ${
                    isActive ? 'text-red-400' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <div className="absolute inset-0 rounded-xl" style={{
                        background: 'linear-gradient(135deg, rgba(239,68,68,0.12), rgba(249,115,22,0.05))',
                        border: '1px solid rgba(239,68,68,0.25)',
                      }} />
                    )}
                    {!isActive && (
                      <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ background: 'rgba(255,255,255,0.03)' }} />
                    )}
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full bg-red-400" style={{ boxShadow: '0 0 10px #f87171' }} />
                    )}
                    <Icon size={17} className={`relative z-10 ${isActive ? 'text-red-400' : 'text-[var(--text-dim)] group-hover:text-[var(--text-secondary)]'}`} />
                    <span className="relative z-10">{label}</span>
                    {isActive && (
                      <div className="relative z-10 ml-auto w-1.5 h-1.5 rounded-full bg-red-400" style={{ boxShadow: '0 0 8px #f87171' }} />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Back to app */}
      <div className="py-3 px-3 space-y-0.5" style={{ borderTop: '1px solid var(--border-color)' }}>
        <NavLink to="/dashboard" className="flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-medium text-[var(--text-dim)] hover:text-[var(--text-primary)] hover:bg-[rgba(255,255,255,0.03)] transition-all duration-200">
          <ArrowLeft size={15} />
          <span>Back to App</span>
        </NavLink>
      </div>

      {/* User profile */}
      {user && (
        <div className="p-4" style={{ borderTop: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.3)',
              }}>
                <Shield size={14} className="text-red-400" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full border-2" style={{ borderColor: 'var(--bg-deepest)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[var(--text-primary)] text-xs font-medium truncate">{user.username}</p>
              <p className="text-red-400/80 text-[10px] truncate">Administrator</p>
            </div>
            <button onClick={handleLogout} className="p-1.5 text-[var(--text-dim)] hover:text-red-400 rounded-md hover:bg-red-400/10 transition-all" title="Logout">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 flex items-center justify-between px-4 z-40" style={{
        background: 'var(--sidebar-bg, rgba(10,13,31,0.95))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--border-color)',
      }}>
        <button onClick={() => setMobileOpen(true)} className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg hover:bg-white/5">
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: RED_GRADIENT }}>
            <Shield size={13} className="text-white" />
          </div>
          <span className="font-display text-red-400 font-bold text-sm">Admin Panel</span>
        </div>
        <div className="w-8" />
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-3 top-3 bottom-3 w-[220px] flex-col z-50 overflow-hidden rounded-2xl" style={{
        background: 'var(--sidebar-bg, rgba(10,13,31,0.95))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--border-color)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      }}>
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[280px] animate-slide-in overflow-y-auto" style={{
            background: 'var(--sidebar-bg, rgba(10,13,31,0.98))',
            borderRight: '1px solid var(--border-color)',
          }}>
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}

